import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantApi } from '../../lib/tenant-api';
import {
  downloadSDLPayloadAsJson,
  DISTRIBUTABLE_SDL_FILENAME,
} from '../../lib/sdl-export';
import { PageLoader } from '../common';
import { toast } from 'react-toastify';
import { FileText, Download, Loader2, Sparkles, RefreshCw, Braces, ChevronDown, Globe } from 'lucide-react';
import type { SDLMetadataOption, SDLComparisonResponse } from '../../lib/api-types';

const LLMS_METADATA_OPTIONS = [
  { key: 'entity_model_summary', label: 'Entity Model Summary' },
  { key: 'variables', label: 'Variables' },
  { key: 'strategic_foci', label: 'Strategic Foci' },
  { key: 'competitors', label: 'Competitors' },
  { key: 'brand', label: 'Brand Name' },
];

const DEFAULT_INCLUDED_METADATA = ['entity_model_summary', 'variables'];
const SDL_METADATA_OPTIONS = [
  ...LLMS_METADATA_OPTIONS,
  { key: 'industry', label: 'Industry' },
  { key: 'last_updated', label: 'Last Updated' },
];
const DEFAULT_EXCLUDED_SDL_METADATA = ['competitors', 'strategic_foci'];
const DEFAULT_INCLUDED_SDL_METADATA: SDLMetadataOption[] = SDL_METADATA_OPTIONS.filter(
  option => !DEFAULT_EXCLUDED_SDL_METADATA.includes(option.key),
).map(option => option.key as SDLMetadataOption);

interface MetadataDropdownProps {
  included: string[];
  onSave: (selection: string[]) => void;
  options: Array<{ key: string; label: string }>;
  title: string;
  disabled?: boolean;
  variant: 'primary' | 'secondary';
}

const MetadataDropdown: React.FC<MetadataDropdownProps> = ({
  included,
  onSave,
  options,
  title,
  disabled,
  variant,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(included);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleToggleOpen = () => {
    if (!isOpen) {
      setDraft(included);
    }
    setIsOpen(!isOpen);
  };

  const toggleKey = (key: string) => {
    setDraft(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
  };

  const handleSave = () => {
    onSave(draft);
    setIsOpen(false);
    toast.success('Metadata selection saved for the next generation.');
  };

  const triggerClasses =
    variant === 'primary'
      ? 'inline-flex items-center justify-center px-2 py-2 border border-transparent border-l border-l-indigo-500 shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed'
      : 'inline-flex items-center justify-center px-2 py-2 border border-gray-300 border-l-0 shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={handleToggleOpen}
        disabled={disabled}
        title={`Choose ${title} to include`}
        aria-label={`Choose ${title} to include`}
        className={`${triggerClasses} h-full`}
      >
        <ChevronDown className="h-4 w-4" />
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 shadow-lg z-10 p-3 text-left">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Include {title}
          </p>
          <div className="space-y-2">
            {options.map(option => (
              <label key={option.key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.includes(option.key)}
                  onChange={() => toggleKey(option.key)}
                  className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                />
                {option.label}
              </label>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-gray-200 flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AEOSection: React.FC = () => {
  const queryClient = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingDistributableSDL, setIsDownloadingDistributableSDL] = useState(false);
  const [includedMetadata, setIncludedMetadata] = useState<string[]>(DEFAULT_INCLUDED_METADATA);
  const [includedSDLMetadata, setIncludedSDLMetadata] = useState<SDLMetadataOption[]>(
    DEFAULT_INCLUDED_SDL_METADATA,
  );

  const { data: cacheStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['aeo-status'],
    queryFn: tenantApi.getLLMSStatus,
    refetchInterval: isGenerating ? 3000 : false,
  });

  useEffect(() => {
    const generatingFlag = localStorage.getItem('llms_generating');
    if (generatingFlag === 'true') {
      setIsGenerating(true);
    }
  }, []);

  useEffect(() => {
    if (isGenerating && cacheStatus?.is_cache_valid) {
      setIsGenerating(false);
      localStorage.removeItem('llms_generating');
      toast.success('LLMS file generated successfully!');
    }
  }, [isGenerating, cacheStatus]);

  useEffect(() => {
    if (cacheStatus?.included_metadata != null) {
      setIncludedMetadata(cacheStatus.included_metadata);
    }
  }, [cacheStatus?.included_metadata]);

  const generateMutation = useMutation({
    mutationFn: (payload: { included_metadata: string[] }) => tenantApi.generateLLMSFile(payload),
    onSuccess: () => {
      setIsGenerating(true);
      localStorage.setItem('llms_generating', 'true');
      toast.info('LLMS file generation started! This may take a minute...');
    },
    onError: (error: any) => {
      toast.error(`Failed to start LLMS generation: ${error.message}`);
      localStorage.removeItem('llms_generating');
    },
  });

  const clearCacheMutation = useMutation({
    mutationFn: tenantApi.clearLLMSCache,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aeo-status'] });
      toast.success('Cache cleared successfully.');
    },
    onError: (error: any) => {
      toast.error(`Failed to clear cache: ${error.message}`);
    },
  });

  const { data: contactInfo, isLoading: contactLoading } = useQuery({
    queryKey: ['contactInfo'],
    queryFn: tenantApi.getContactInfo,
  });
  const contactUrl = contactInfo?.contact_url || '';
  const [urlDraft, setUrlDraft] = useState('');
  const [isEditingUrl, setIsEditingUrl] = useState(false);

  useEffect(() => {
    if (contactLoading) return;
    setUrlDraft(contactUrl);
    if (!contactUrl) {
      setIsEditingUrl(true);
    }
  }, [contactLoading, contactUrl]);

  const saveUrlMutation = useMutation({
    mutationFn: (url: string) => tenantApi.createOrUpdateContactInfo({ contact_url: url }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contactInfo'] });
      setIsEditingUrl(false);
      toast.success('Website URL saved.');
    },
    onError: (error: any) => {
      toast.error(`Failed to save website URL: ${error.message}`);
    },
  });

  const compareMutation = useMutation({
    mutationFn: tenantApi.getSDLComparison,
    onError: (error: any) => {
      toast.error(`Failed to compare deployed SDL: ${error.message}`);
    },
  });

  const handleSaveUrl = () => {
    let url = urlDraft.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      url = `https://${url}`;
    }
    saveUrlMutation.mutate(url);
  };

  const handleCancelEditUrl = () => {
    setUrlDraft(contactUrl);
    setIsEditingUrl(false);
  };

  const handleGenerate = () => {
    generateMutation.mutate({ included_metadata: includedMetadata });
  };

  const handleDownloadDistributableSDL = async () => {
    try {
      setIsDownloadingDistributableSDL(true);
      const sdl = await tenantApi.getFullSDL(includedSDLMetadata);
      downloadSDLPayloadAsJson(sdl, DISTRIBUTABLE_SDL_FILENAME);
      toast.success(`${DISTRIBUTABLE_SDL_FILENAME} downloaded successfully.`);
    } catch (error: any) {
      toast.error(`Failed to build SDL file: ${error.message}`);
    } finally {
      setIsDownloadingDistributableSDL(false);
    }
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);

      const blob = await tenantApi.downloadLLMSFile();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = cacheStatus?.file_name || 'llms.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('LLMS file downloaded successfully!');
    } catch (error: any) {
      if (error.response?.status === 404) {
        toast.error('File not available. Please regenerate first.');
      } else {
        toast.error(`Failed to download file: ${error.message}`);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRegenerate = async () => {
    try {
      await clearCacheMutation.mutateAsync();
      generateMutation.mutate({ included_metadata: includedMetadata });
      setIsGenerating(true);
      localStorage.setItem('llms_generating', 'true');
      toast.info('Regenerating LLMS file...');
    } catch (error: any) {
      toast.error(`Failed to clear cache before regeneration: ${error.message}`);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const renderComparisonResult = (result: SDLComparisonResponse) => {
    const green = 'bg-green-50 border border-green-200 text-green-800 px-4 py-3 text-sm';
    const amber = 'bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 text-sm';
    const red = 'bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm';

    switch (result.status) {
      case 'up_to_date':
        return (
          <div className={green}>
            <p className="font-medium">Deployed SDL is up to date.</p>
            <p className="mt-1">
              {result.deployed_generated_at
                ? `Deployed file generated ${formatDate(result.deployed_generated_at)}.`
                : 'Deployed file predates generation timestamps — matched by content.'}{' '}
              Checked <span className="font-mono">{result.checked_url}</span>.
            </p>
          </div>
        );
      case 'outdated':
        return (
          <div className={amber}>
            <p className="font-medium">Deployed SDL is out of date.</p>
            {result.diff && (
              <p className="mt-1">
                Questions: {result.diff.questions_added} added · {result.diff.questions_removed} removed ·{' '}
                {result.diff.questions_changed} changed
                {result.diff.metadata_changed.length > 0 &&
                  ` — metadata changed: ${result.diff.metadata_changed.join(', ')}`}
                .
              </p>
            )}
            {result.deployed_generated_at && (
              <p className="mt-1">Deployed file generated {formatDate(result.deployed_generated_at)}.</p>
            )}
            <p className="mt-1">
              Re-download the distributable SDL above and re-upload it to your website root.
            </p>
            {result.diff && result.diff.metadata_not_deployed.length > 0 && (
              <p className="mt-1 text-amber-700/70 text-xs">
                Not included in the deployed file (ignored): {result.diff.metadata_not_deployed.join(', ')}.
              </p>
            )}
          </div>
        );
      case 'no_sdl':
        return (
          <div className={red}>
            No <span className="font-mono">{DISTRIBUTABLE_SDL_FILENAME}</span> found at{' '}
            <span className="font-mono">{result.checked_url}</span> (404). Download the file above and upload it
            to your website root.
          </div>
        );
      case 'unreachable':
        return (
          <div className={red}>
            Could not reach <span className="font-mono">{result.checked_url}</span>
            {result.detail ? `: ${result.detail}` : '.'}
          </div>
        );
      case 'invalid_json':
        return (
          <div className={red}>
            <span className="font-mono">{result.checked_url}</span> did not return a valid SDL file
            {result.detail ? `: ${result.detail}` : '.'}
          </div>
        );
      case 'tenant_mismatch':
        return (
          <div className={red}>
            The deployed <span className="font-mono">{DISTRIBUTABLE_SDL_FILENAME}</span> belongs to a different
            tenant. Re-download your SDL above and replace the file on your website.
          </div>
        );
      case 'no_url':
      default:
        return <div className={amber}>Enter and save your website URL above, then compare again.</div>;
    }
  };

  const getDaysSinceGeneration = (dateString: string) => {
    const now = new Date();
    const generated = new Date(dateString);
    const diffTime = Math.abs(now.getTime() - generated.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    return `${diffDays} days ago`;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-bold text-gray-800">Answer Engine Optimization</h2>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Generate and download LLMS.txt, and export distributable Structured Data Layer (SDL) JSON for your site.
      </p>

      {isGenerating && (
        <div className="mb-4 bg-indigo-50 border border-indigo-200  p-4">
          <div className="flex items-center">
            <Loader2 className="h-5 w-5 animate-spin text-indigo-600 mr-3" />
            <div>
              <p className="font-medium text-indigo-900">Generating LLMS file...</p>
              <p className="text-sm text-indigo-700">This may take a minute. You can navigate away and come back later.</p>
            </div>
          </div>
        </div>
      )}

      {statusLoading ? (
        <PageLoader fullScreen={false} />
      ) : (
        <>
      {cacheStatus ? (
        <>
          <div className="bg-gray-50  p-4 border border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <FileText className="h-5 w-5 text-gray-400 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">{cacheStatus.file_name}</h3>
                </div>
                <div className="space-y-1 text-sm text-gray-600">
                  <p>
                    <span className="font-medium">Generated:</span>{' '}
                    {formatDate(cacheStatus.generation_date)}
                  </p>
                  <p>
                    <span className="font-medium">Last Updated:</span>{' '}
                    {getDaysSinceGeneration(cacheStatus.generation_date)}
                  </p>
                  <p>
                    <span className="font-medium">Size:</span>{' '}
                    {formatFileSize(cacheStatus.file_size)}
                  </p>
                  <p>
                    <span className="font-medium">Status:</span>{' '}
                    {isGenerating ? (
                      <span className="text-indigo-600 font-medium">Generating...</span>
                    ) : (
                      <span className="text-green-600 font-medium">Valid</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handleDownload}
                  disabled={isDownloading || isGenerating}
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDownloading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </>
                  )}
                </button>
                <div className="inline-flex items-stretch">
                  <button
                    onClick={handleRegenerate}
                    disabled={isGenerating || clearCacheMutation.isPending || generateMutation.isPending}
                    className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isGenerating || clearCacheMutation.isPending || generateMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Regenerating...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Regenerate
                      </>
                    )}
                  </button>
                  <MetadataDropdown
                    included={includedMetadata}
                    onSave={setIncludedMetadata}
                    options={LLMS_METADATA_OPTIONS}
                    title="LLMS metadata"
                    disabled={isGenerating || clearCacheMutation.isPending || generateMutation.isPending}
                    variant="secondary"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-indigo-50 border border-indigo-200  p-4">
            <h4 className="text-sm font-medium text-indigo-900 mb-2">About LLMS.txt</h4>
            <p className="text-sm text-indigo-800">
              The LLMS.txt bundle contains categorized questions and answers optimized for AI search engines.
              Use the installation steps below to publish it at the root of your website.
            </p>
          </div>
        </>
      ) : (
        <div className="bg-gray-50  p-12 border border-gray-200">
          <div className="flex flex-col items-center justify-center text-center">
            <FileText className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No LLMS File Generated</h3>
            <p className="text-sm text-gray-600 mb-6 max-w-md">
              Generate your first LLMS.txt file to optimize your content for AI search engines and get actionable insights.
            </p>
            <div className="inline-flex items-stretch">
              <button
                onClick={handleGenerate}
                disabled={isGenerating || generateMutation.isPending}
                className="inline-flex items-center px-6 py-3 border border-transparent shadow-sm text-base font-medium  text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating || generateMutation.isPending ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" />
                    Generate LLMS File
                  </>
                )}
              </button>
              <MetadataDropdown
                included={includedMetadata}
                onSave={setIncludedMetadata}
                options={LLMS_METADATA_OPTIONS}
                title="LLMS metadata"
                disabled={isGenerating || generateMutation.isPending}
                variant="primary"
              />
            </div>
          </div>
        </div>
      )}

          <div className="mt-8 bg-white p-4 border border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <Braces className="h-5 w-5 text-gray-400 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Distributed SDL ({DISTRIBUTABLE_SDL_FILENAME})</h3>
                </div>
                <p className="text-sm text-gray-600">
                  Build the same all-questions SDL JSON you can export from Primer Questions → Settings → LAWMA Labs,
                  using your live question list. Host this file as <span className="font-mono text-gray-800">/{DISTRIBUTABLE_SDL_FILENAME}</span> on your site.
                </p>
              </div>
              <div className="inline-flex items-stretch">
                <button
                  type="button"
                  onClick={handleDownloadDistributableSDL}
                  disabled={isDownloadingDistributableSDL}
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shrink-0"
                >
                  {isDownloadingDistributableSDL ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Preparing…
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Generate and Download Distributable SDL
                    </>
                  )}
                </button>
                <MetadataDropdown
                  included={includedSDLMetadata}
                  onSave={selection => setIncludedSDLMetadata(selection as SDLMetadataOption[])}
                  options={SDL_METADATA_OPTIONS}
                  title="SDL metadata"
                  disabled={isDownloadingDistributableSDL}
                  variant="primary"
                />
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 mb-1">Deployed SDL status</h4>
              <p className="text-xs text-gray-500 mb-3">
                Enter your website address — we automatically look for{' '}
                <span className="font-mono">{DISTRIBUTABLE_SDL_FILENAME}</span> at its root, no need to add it yourself.
                The URL is shared with Settings → Contacts.
              </p>

              {contactLoading ? (
                <div className="flex items-center text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading website URL...
                </div>
              ) : isEditingUrl ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="inline-flex items-stretch w-full sm:max-w-md">
                    <input
                      type="text"
                      value={urlDraft}
                      onChange={e => setUrlDraft(e.target.value)}
                      placeholder="yourdomain.com"
                      className="flex-1 min-w-0 border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {!urlDraft.trim().endsWith('.json') && (
                      <span className="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 text-sm text-gray-500 font-mono whitespace-nowrap">
                        /{DISTRIBUTABLE_SDL_FILENAME}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleSaveUrl}
                    disabled={saveUrlMutation.isLoading || !urlDraft.trim()}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saveUrlMutation.isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save URL'
                    )}
                  </button>
                  {contactUrl && (
                    <button
                      type="button"
                      onClick={handleCancelEditUrl}
                      className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center text-sm text-gray-800">
                    <Globe className="h-4 w-4 text-gray-400 mr-2 shrink-0" />
                    <span className="font-mono break-all">{contactUrl.replace(/\/+$/, '')}</span>
                    {!contactUrl.trim().endsWith('.json') && (
                      <span className="font-mono text-gray-400">/{DISTRIBUTABLE_SDL_FILENAME}</span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsEditingUrl(true)}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
                  >
                    Edit
                  </button>
                </div>
              )}

              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => compareMutation.mutate()}
                  disabled={!contactUrl || isEditingUrl || compareMutation.isLoading}
                  title={!contactUrl ? 'Save your website URL first' : undefined}
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {compareMutation.isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Comparing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Compare deployed SDL
                    </>
                  )}
                </button>
              </div>

              {compareMutation.data && !compareMutation.isLoading && (
                <div className="mt-3">{renderComparisonResult(compareMutation.data)}</div>
              )}
            </div>
          </div>

          <div className="mt-6 bg-gray-50 border border-gray-200 p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Publishing &amp; installation</h3>

            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">{DISTRIBUTABLE_SDL_FILENAME} file</h4>
              <p className="text-sm text-gray-700 mb-3">
                LAWMA builds an <span className="font-mono">{DISTRIBUTABLE_SDL_FILENAME}</span> file containing structured Q&amp;A data
                optimized for AI search engines like ChatGPT, Gemini, Perplexity, and Claude. This file helps AI platforms understand and recommend your brand.
              </p>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Implementation</p>
              <ol className="list-decimal list-inside text-sm text-gray-700 space-y-2">
                <li>Download your <span className="font-mono">{DISTRIBUTABLE_SDL_FILENAME}</span> file from the LAWMA Admin Portal (Tenant Settings → AEO tab, or Primer Questions → Settings → LAWMA Labs).</li>
                <li>Upload the file to your website&apos;s root directory.</li>
                <li>Verify the file is publicly accessible at <span className="font-mono">https://yourdomain.com/{DISTRIBUTABLE_SDL_FILENAME}</span> (replace with your domain).</li>
              </ol>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">LLMS.txt installation</h4>
              <ol className="list-decimal list-inside text-sm text-gray-700 space-y-2">
                <li>Upload <span className="font-mono">llms.txt</span> (from the downloaded bundle) to your web root folder.</li>
                <li>Verify it is publicly accessible by visiting the URL directly (for example <span className="font-mono">https://lawma.ai/llms.txt</span>).</li>
              </ol>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AEOSection;
